import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Store, ArrowRight, Star, BadgeCheck, Shield, Sparkles } from 'lucide-react';
import { API } from '../App';

export default function FeaturedVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/vendors/featured?limit=4`)
      .then((r) => {
        if (!cancelled) setVendors(r.data.vendors || []);
      })
      .catch(() => {
        if (!cancelled) setVendors([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="py-16 md:py-24 bg-muted/20" data-testid="featured-vendors-loading">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="h-8 bg-muted rounded w-64 mb-12 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {['v1','v2','v3','v4'].map((k) => (
              <div key={k} className="h-72 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (vendors.length === 0) {
    return null;
  }

  return (
    <section
      className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-background to-amber-50/30"
      data-testid="featured-vendors-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2 flex items-center gap-2">
              <Sparkles className="h-3 w-3" />
              Spotlight This Week
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-foreground">
              Featured Vendors
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Trusted Christian-led businesses bringing their craft to the global marketplace.
            </p>
          </div>
          <Link to="/products">
            <Button variant="ghost" className="text-primary hover:text-primary/80">
              Browse All Vendors <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {vendors.map((v) => (
            <Link
              key={v.vendor_id}
              to={`/store/${v.store_slug || v.vendor_id}`}
              className="group block"
              data-testid={`featured-vendor-${v.store_slug || v.vendor_id}`}
            >
              <Card className="overflow-hidden hover:shadow-lg transition-shadow border-primary/10 h-full flex flex-col">
                {/* Preview product image collage */}
                <div className="relative aspect-square bg-muted">
                  {v.preview_products && v.preview_products.length > 0 ? (
                    v.preview_products.length === 1 ? (
                      <img
                        src={v.preview_products[0].images?.[0]}
                        alt={v.preview_products[0].name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                    ) : (
                      <div className="grid grid-cols-2 grid-rows-2 gap-0.5 w-full h-full">
                        {v.preview_products.slice(0, 4).map((p, idx) => (
                          <img
                            key={`${v.vendor_id}-prev-${idx}`}
                            src={p.images?.[0]}
                            alt={p.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            loading="lazy"
                          />
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Store className="h-16 w-16 text-muted-foreground/30" />
                    </div>
                  )}
                  {v.is_featured && (
                    <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground border-0 gap-1 shadow">
                      <Sparkles className="h-3 w-3" />
                      Featured
                    </Badge>
                  )}
                  {v.is_founding_vendor && !v.is_featured && (
                    <Badge className="absolute top-2 left-2 bg-gradient-to-r from-amber-500 to-amber-700 text-white border-0 gap-1 shadow">
                      <Star className="h-3 w-3 fill-white" />
                      Founding
                    </Badge>
                  )}
                </div>

                <CardContent className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <h3 className="font-heading font-semibold text-base leading-tight line-clamp-1 group-hover:text-primary transition-colors">
                      {v.business_name}
                    </h3>
                    {v.verification === 'verified' && (
                      <BadgeCheck className="h-4 w-4 text-green-600 shrink-0" />
                    )}
                    {v.verification === 'trusted' && (
                      <Shield className="h-4 w-4 text-amber-600 shrink-0" />
                    )}
                  </div>
                  {v.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                      {v.description}
                    </p>
                  )}
                  <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
                    <span>{v.product_count || 0} listings</span>
                    {v.country && <span>{v.country}</span>}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
